import {
    contributionCaptureAuthorityKey,
    DISABLED_CONTRIBUTION_CAPTURE_CONFIG,
    isContributionCaptureAllowedForProject,
    normalizeContributionCaptureConfig,
} from "./config";
import { createCaptureClock, createCaptureId, type CaptureClock } from "./identity";
import {
    IndexedDbContributionCaptureStore,
    type ContributionCaptureStore,
} from "./store";
import {
    ContributionBatchTransport,
    type ContributionAbandonFlushSummary,
    type ContributionFlushSummary,
} from "./transport";
import type {
    AnyContributionEventEnvelopeV1,
    ContributionCommittedOperationInputV1,
    ContributionCaptureHealth,
    ContributionCaptureRuntimeOptions,
    ContributionCheckpointDraftV1,
    ContributionCheckpointReason,
    ContributionCheckpointV1,
    ContributionCompositionSessionContext,
    ContributionEpisodeCreatePayloadV1,
    ContributionEpisodeAbandonReason,
    ContributionEventPayloadMap,
    ContributionEventEnvelopeV1,
    ContributionEventType,
    ContributionSessionEndReason,
    ContributionCaptureScopesV1,
    ContributionJsonValue,
    NormalizedContributionCaptureConfigV1,
} from "./types";

interface ContributionTransportLike {
    configure(runtime: {
        http: ContributionCaptureRuntimeOptions["http"];
        getToken: ContributionCaptureRuntimeOptions["getToken"];
        ownerUid: string | null;
    }): void;
    invalidate(): void;
    flushAbandonments(): Promise<ContributionAbandonFlushSummary>;
    flush(config: NormalizedContributionCaptureConfigV1): Promise<ContributionFlushSummary>;
}

interface ActiveContributionSession {
    ownerUid: string;
    clientEpisodeId: string;
    sessionId: string;
    context: ContributionCompositionSessionContext;
    config: NormalizedContributionCaptureConfigV1;
    clock: CaptureClock;
}

type HealthListener = (health: ContributionCaptureHealth) => void;

const CONTENT_FIELD_KEYS = new Set([
    "correctedtext",
    "filename",
    "goal",
    "label",
    "name",
    "speakername",
    "text",
    "title",
    "uri",
    "url",
]);

const SOURCE_REFERENCE_KEYS = new Set([
    "assetid",
    "sourcebucket",
    "sourceid",
    "sourceobject",
    "thumbnailid",
    "trackid",
]);

const RENDER_REFERENCE_KEYS = new Set([
    "exportid",
    "outputurl",
    "renderid",
]);

/**
 * Scope checks happen immediately before an operation enters durable storage.
 * Keeping this at the recorder boundary protects every current and future
 * UndoManager adapter, rather than relying on each caller to remember to redact.
 */
const sanitizeOperationValue = (
    value: ContributionJsonValue,
    scopes: ContributionCaptureScopesV1,
): ContributionJsonValue => {
    if (Array.isArray(value)) {
        return value.map((item) => sanitizeOperationValue(item, scopes));
    }
    if (!value || typeof value !== "object") return value;

    const output: { [key: string]: ContributionJsonValue } = {};
    for (const [key, item] of Object.entries(value)) {
        const normalizedKey = key.toLowerCase();
        if (!scopes.contentFields && CONTENT_FIELD_KEYS.has(normalizedKey)) continue;
        if (!scopes.sourceMediaReferences && SOURCE_REFERENCE_KEYS.has(normalizedKey)) continue;
        if (!scopes.renderReferences && RENDER_REFERENCE_KEYS.has(normalizedKey)) continue;
        output[key] = sanitizeOperationValue(item, scopes);
    }
    return output;
};

const initialHealth = (): ContributionCaptureHealth => ({
    status: "disabled",
    enabled: false,
    activeEpisodeId: null,
    queueDepth: 0,
    quarantinedItemCount: 0,
    lastErrorCode: null,
    lastFlushAt: null,
    lastSuccessfulFlushAt: null,
});

export class ContributionCaptureService {
    private config: NormalizedContributionCaptureConfigV1 = {
        ...DISABLED_CONTRIBUTION_CAPTURE_CONFIG,
        scopes: { ...DISABLED_CONTRIBUTION_CAPTURE_CONFIG.scopes },
    };
    private activeSession: ActiveContributionSession | null = null;
    private ownerUid: string | null = null;
    private health: ContributionCaptureHealth = initialHealth();
    private readonly healthListeners = new Set<HealthListener>();
    private timerId: ReturnType<typeof setInterval> | null = null;
    private started = false;
    private captureSuspended = false;
    private flushInFlight: Promise<ContributionFlushSummary> | null = null;
    private readonly producerId = createCaptureId("producer");
    private readonly onlineHandler = () => { void this.flush(); };
    private readonly visibilityHandler = () => {
        if (typeof document === "undefined" || document.visibilityState === "visible") {
            void this.flush();
        }
    };

    constructor(
        private readonly store: ContributionCaptureStore = new IndexedDbContributionCaptureStore(),
        private readonly transport: ContributionTransportLike = new ContributionBatchTransport(store),
    ) {}

    configure(options: ContributionCaptureRuntimeOptions): void {
        const nextConfig = normalizeContributionCaptureConfig(options.rawConfig);
        const nextOwnerUid = String(options.ownerUid ?? "").trim() || null;
        const changedCaptureAuthority =
            contributionCaptureAuthorityKey(this.config) !== contributionCaptureAuthorityKey(nextConfig) ||
            this.ownerUid !== nextOwnerUid;

        // A capture authority change is prospective. Stop using the old context immediately;
        // callers should close an active session before applying a replacement configuration.
        if (changedCaptureAuthority && this.activeSession) {
            this.transport.invalidate();
            this.activeSession = null;
        }

        this.config = nextConfig;
        this.ownerUid = nextOwnerUid;
        this.captureSuspended = false;
        this.transport.configure({
            http: options.http,
            getToken: options.getToken,
            ownerUid: nextOwnerUid,
        });
        if (this.started) this.restartTimer();
        const effectivelyEnabled = nextConfig.enabled && Boolean(nextOwnerUid);
        this.setHealth({
            enabled: effectivelyEnabled,
            activeEpisodeId: this.activeSession?.clientEpisodeId ?? null,
            status: effectivelyEnabled ? "idle" : "disabled",
            lastErrorCode: null,
        });
        void this.refreshQueueDepth();
    }

    suspendCapture(): void {
        this.captureSuspended = true;
        this.activeSession = null;
        this.transport.invalidate();
        this.setHealth({ activeEpisodeId: null });
    }

    async abandonCaptureAuthority(reason: ContributionEpisodeAbandonReason): Promise<void> {
        const ownerUid = this.ownerUid;
        const authorityKey = contributionCaptureAuthorityKey(this.config);
        // Close the recorder gate before the first await. Composition restoration
        // may otherwise start another old-authority episode during the sweep.
        this.suspendCapture();
        if (!ownerUid) return;
        const payload = { schemaVersion: 1 as const, reason, endedAt: new Date().toISOString() };
        try {
            await this.store.requestAuthorityAbandon(ownerUid, authorityKey, payload);
            await this.refreshQueueDepth();
            const result = await this.transport.flushAbandonments();
            if (result.errorCode && result.errorCode !== "offline" && result.errorCode !== "missing_token") {
                this.setHealth({ status: "degraded", lastErrorCode: result.errorCode });
            }
        } catch {
            this.setHealth({ status: "degraded", lastErrorCode: "abandonment_failure" });
        }
    }

    async reconcileCaptureAuthority(): Promise<void> {
        const ownerUid = this.ownerUid;
        if (!ownerUid) return;
        const authorityKey = this.config.enabled ? contributionCaptureAuthorityKey(this.config) : null;
        const payload = {
            schemaVersion: 1 as const,
            reason: (this.config.enabled ? "capture_config_replaced" : "capture_disabled") as ContributionEpisodeAbandonReason,
            endedAt: new Date().toISOString(),
        };
        try {
            await this.store.requestUnauthorizedEpisodesAbandon(ownerUid, authorityKey, payload);
            await this.refreshQueueDepth();
            await this.transport.flushAbandonments();
        } catch {
            this.setHealth({ status: "degraded", lastErrorCode: "abandonment_failure" });
        }
    }

    startTransport(): void {
        if (this.started) return;
        this.started = true;
        this.restartTimer();
        if (typeof window !== "undefined") window.addEventListener("online", this.onlineHandler);
        if (typeof document !== "undefined") {
            document.addEventListener("visibilitychange", this.visibilityHandler);
        }
        // Abandonment is a control-plane operation and must retry even while
        // capture itself is disabled.
        if (this.ownerUid) void this.flush();
    }

    stopTransport(): void {
        this.started = false;
        if (this.timerId != null) clearInterval(this.timerId);
        this.timerId = null;
        if (typeof window !== "undefined") window.removeEventListener("online", this.onlineHandler);
        if (typeof document !== "undefined") {
            document.removeEventListener("visibilitychange", this.visibilityHandler);
        }
    }

    isEnabledForProject(projectId: string): boolean {
        return !this.captureSuspended && Boolean(this.ownerUid) &&
            isContributionCaptureAllowedForProject(this.config, String(projectId ?? "").trim());
    }

    getActiveSessionContext(): ContributionCompositionSessionContext | null {
        return this.activeSession ? { ...this.activeSession.context } : null;
    }

    async recordOperation(input: ContributionCommittedOperationInputV1): Promise<string | null> {
        if (!this.activeSession) return null;
        const scopes = this.activeSession.config.scopes;
        const operationId = String(input.operationId ?? "").trim() || createCaptureId("operation");
        const targets = (input.targets ?? [])
            .map((target) => ({
                entityType: target.entityType,
                entityId: String(target.entityId ?? "").trim(),
            }))
            .filter((target) => target.entityId.length > 0);
        const eventId = await this.recordEvent(
            "project.operation_committed",
            {
                operationSchemaVersion: 1,
                operationId,
                operationKind: input.operationKind,
                targets,
                ...(input.transactionId ? { transactionId: String(input.transactionId) } : {}),
                ...(input.before !== undefined
                    ? { before: sanitizeOperationValue(input.before, scopes) }
                    : {}),
                ...(input.after !== undefined
                    ? { after: sanitizeOperationValue(input.after, scopes) }
                    : {}),
                ...(input.details !== undefined
                    ? {
                        details: sanitizeOperationValue(input.details, scopes) as {
                            [key: string]: ContributionJsonValue;
                        },
                    }
                    : {}),
                ...(input.reversesOperationId
                    ? { reversesOperationId: String(input.reversesOperationId) }
                    : {}),
                ...(input.reappliesOperationId
                    ? { reappliesOperationId: String(input.reappliesOperationId) }
                    : {}),
                ...(input.childOperationIds
                    ? { childOperationIds: input.childOperationIds.map(String).filter(Boolean) }
                    : {}),
            },
            input.origin ?? "human",
        );
        return eventId ? operationId : null;
    }

    async startCompositionSession(
        context: ContributionCompositionSessionContext,
        initialCheckpoint?: ContributionCheckpointDraftV1,
    ): Promise<string | null> {
        const projectId = String(context.projectId ?? "").trim();
        const compositionId = String(context.compositionId ?? "").trim();
        const ownerUid = this.ownerUid;
        if (!ownerUid || !projectId || !compositionId) return null;
        if (this.captureSuspended) return null;
        if (!isContributionCaptureAllowedForProject(this.config, projectId)) return null;
        const authorityKey = contributionCaptureAuthorityKey(this.config);

        if (this.activeSession) {
            if (
                this.activeSession.context.projectId === projectId &&
                this.activeSession.context.compositionId === compositionId
            ) {
                return this.activeSession.clientEpisodeId;
            }
            await this.endCompositionSession({ reason: "composition_switched" });
        }

        const clock = createCaptureClock();
        const firstReading = clock.read();
        const clientEpisodeId = createCaptureId("episode");
        const sessionId = createCaptureId("session");
        const normalizedContext: ContributionCompositionSessionContext = {
            workspaceId: String(context.workspaceId ?? "").trim() || undefined,
            projectId,
            compositionId,
            role: String(context.role ?? "").trim() || undefined,
            readOnly: Boolean(context.readOnly),
            appRelease: String(context.appRelease ?? "").trim() || "unknown",
        };
        const payload: ContributionEpisodeCreatePayloadV1 = {
            schemaVersion: 1,
            clientEpisodeId,
            sessionId,
            profile: this.config.profile,
            captureConfigId: this.config.captureConfigId,
            consentScopeId: this.config.consentScopeId,
            ...normalizedContext,
            startedAt: firstReading.occurredAt,
        };

        try {
            await this.store.createEpisode(ownerUid, payload, authorityKey);
            if (
                this.captureSuspended ||
                this.ownerUid !== ownerUid ||
                contributionCaptureAuthorityKey(this.config) !== authorityKey
            ) {
                // Creation can finish after revocation's first atomic sweep. Sweep
                // again before returning so the late row cannot be uploaded later.
                await this.store.requestAuthorityAbandon(ownerUid, authorityKey, {
                    schemaVersion: 1,
                    reason: "capture_authority_changed",
                    endedAt: new Date().toISOString(),
                });
                this.flushSoon();
                return null;
            }
            this.activeSession = {
                ownerUid,
                clientEpisodeId,
                sessionId,
                context: normalizedContext,
                config: this.config,
                clock,
            };
            this.setHealth({
                status: "healthy",
                enabled: true,
                activeEpisodeId: clientEpisodeId,
                lastErrorCode: null,
            });
            await this.recordEvent("collection.session_started", {
                startedAt: firstReading.occurredAt,
                readOnly: normalizedContext.readOnly,
                ...(normalizedContext.role ? { role: normalizedContext.role } : {}),
            });
            await this.recordEvent("project.opened", {
                openedAt: clock.read().occurredAt,
                checkpointExpected: Boolean(initialCheckpoint && this.config.scopes.projectCheckpoints),
            });
            if (initialCheckpoint && this.config.scopes.projectCheckpoints) {
                await this.recordCheckpoint("session_start", initialCheckpoint);
            }
            this.flushSoon();
            return clientEpisodeId;
        } catch {
            this.activeSession = null;
            this.setHealth({
                status: "degraded",
                enabled: true,
                activeEpisodeId: null,
                lastErrorCode: "storage_unavailable",
            });
            return null;
        }
    }

    async endCompositionSession(args: {
        reason: ContributionSessionEndReason;
        finalCheckpoint?: ContributionCheckpointDraftV1;
    }): Promise<void> {
        const session = this.activeSession;
        if (!session) return;
        try {
            if (args.finalCheckpoint && session.config.scopes.projectCheckpoints) {
                await this.recordCheckpoint("session_end", args.finalCheckpoint);
            }
            const reading = session.clock.read();
            await this.recordEvent("project.closed", {
                closedAt: reading.occurredAt,
                reason: args.reason,
                checkpointExpected: Boolean(args.finalCheckpoint && session.config.scopes.projectCheckpoints),
            });
            const endedAt = session.clock.read().occurredAt;
            await this.recordEvent("collection.session_ended", {
                endedAt,
                reason: args.reason,
            });
            await this.store.requestEpisodeComplete(session.ownerUid, session.clientEpisodeId, {
                schemaVersion: 1,
                reason: args.reason,
                endedAt,
            });
        } catch {
            this.setHealth({ status: "degraded", lastErrorCode: "storage_unavailable" });
        } finally {
            if (this.activeSession?.clientEpisodeId === session.clientEpisodeId) {
                this.activeSession = null;
            }
            this.setHealth({ activeEpisodeId: null });
            await this.refreshQueueDepth();
            this.flushSoon();
        }
    }

    async recordCheckpoint(
        reason: ContributionCheckpointReason,
        draft: ContributionCheckpointDraftV1,
    ): Promise<string | null> {
        const session = this.activeSession;
        if (!session || !session.config.scopes.projectCheckpoints) return null;
        const reading = session.clock.read();
        const checkpoint: ContributionCheckpointV1 = {
            schemaVersion: 1,
            checkpointId: createCaptureId("checkpoint"),
            clientEpisodeId: session.clientEpisodeId,
            sessionId: session.sessionId,
            reason,
            occurredAt: reading.occurredAt,
            monotonicMs: reading.monotonicMs,
            projectId: session.context.projectId,
            compositionId: session.context.compositionId,
            ...draft,
        };
        try {
            const enqueued = await this.store.addCheckpoint(
                session.ownerUid,
                checkpoint,
                session.config.maxQueueItems,
            );
            if (!enqueued) {
                this.setHealth({ status: "degraded", lastErrorCode: "queue_capacity" });
                return null;
            }
            await this.refreshQueueDepth();
            return checkpoint.checkpointId;
        } catch {
            this.setHealth({ status: "degraded", lastErrorCode: "storage_unavailable" });
            return null;
        }
    }

    async flush(): Promise<ContributionFlushSummary> {
        if (this.flushInFlight) return this.flushInFlight;
        this.flushInFlight = this.transport.flushAbandonments()
            .then(async (abandonment) => ({
                abandonment,
                summary: this.captureSuspended
                    ? {
                        status: "skipped" as const,
                        sentEvents: 0,
                        sentCheckpoints: 0,
                        completedEpisodes: 0,
                        queueDepth: this.health.queueDepth,
                        quarantinedItemCount: this.health.quarantinedItemCount,
                    }
                    : await this.transport.flush(this.config),
            }))
            .then(({ abandonment, summary }) => {
                const now = new Date().toISOString();
                const errorCode = abandonment.errorCode ?? summary.errorCode;
                const benignSkip = summary.status === "skipped" && (
                    !errorCode ||
                    errorCode === "missing_token" ||
                    errorCode === "transport_unconfigured"
                );
                const effectivelyEnabled = this.config.enabled && Boolean(this.ownerUid);
                this.setHealth({
                    status: !effectivelyEnabled
                        ? "disabled"
                        : errorCode || summary.status === "failed" || summary.status === "partial"
                            ? "degraded"
                            : benignSkip ? "idle" : "healthy",
                    enabled: effectivelyEnabled,
                    queueDepth: summary.queueDepth,
                    quarantinedItemCount: summary.quarantinedItemCount,
                    lastErrorCode: errorCode ?? null,
                    lastFlushAt: now,
                    ...(summary.status === "success" && !errorCode
                        ? { lastSuccessfulFlushAt: now }
                        : {}),
                });
                return errorCode ? { ...summary, errorCode } : summary;
            })
            .catch(() => {
                const summary: ContributionFlushSummary = {
                    status: "failed",
                    sentEvents: 0,
                    sentCheckpoints: 0,
                    completedEpisodes: 0,
                    queueDepth: this.health.queueDepth,
                    quarantinedItemCount: this.health.quarantinedItemCount,
                    errorCode: "transport_failure",
                };
                this.setHealth({
                    status: "degraded",
                    lastErrorCode: summary.errorCode ?? null,
                    lastFlushAt: new Date().toISOString(),
                });
                return summary;
            })
            .finally(() => {
                this.flushInFlight = null;
            });
        return this.flushInFlight;
    }

    getHealth(): ContributionCaptureHealth {
        return { ...this.health };
    }

    subscribeHealth(listener: HealthListener): () => void {
        this.healthListeners.add(listener);
        try { listener(this.getHealth()); } catch {}
        return () => this.healthListeners.delete(listener);
    }

    private async recordEvent<T extends ContributionEventType>(
        eventType: T,
        payload: ContributionEventPayloadMap[T],
        origin: AnyContributionEventEnvelopeV1["origin"] = "system",
    ): Promise<string | null> {
        const session = this.activeSession;
        if (!session || !session.config.scopes.semanticEvents) return null;
        try {
            const result = await this.store.appendEvent(
                session.ownerUid,
                session.clientEpisodeId,
                this.producerId,
                session.config.maxQueueItems,
                ({ producerId, producerSeq }) => {
                    const reading = session.clock.read();
                    const envelope: ContributionEventEnvelopeV1<T> = {
                        schemaVersion: 1,
                        eventVersion: 1,
                        eventId: createCaptureId("event"),
                        eventType,
                        clientEpisodeId: session.clientEpisodeId,
                        sessionId: session.sessionId,
                        producerId,
                        producerSeq,
                        occurredAt: reading.occurredAt,
                        monotonicMs: reading.monotonicMs,
                        appRelease: session.context.appRelease,
                        captureConfigId: session.config.captureConfigId,
                        consentScopeId: session.config.consentScopeId,
                        profile: session.config.profile,
                        ...(session.context.workspaceId ? { workspaceId: session.context.workspaceId } : {}),
                        projectId: session.context.projectId,
                        compositionId: session.context.compositionId,
                        origin,
                        payload,
                    };
                    return envelope as AnyContributionEventEnvelopeV1;
                },
            );
            if (!result.enqueued) {
                this.setHealth({
                    status: "degraded",
                    lastErrorCode: result.reason === "capacity" ? "queue_capacity" : "missing_episode",
                    queueDepth: result.queueDepth,
                });
                return null;
            }
            this.setHealth({
                status: "healthy",
                lastErrorCode: null,
                queueDepth: result.queueDepth,
            });
            return result.event?.eventId ?? null;
        } catch {
            this.setHealth({ status: "degraded", lastErrorCode: "storage_unavailable" });
            return null;
        }
    }

    private restartTimer(): void {
        if (this.timerId != null) clearInterval(this.timerId);
        this.timerId = null;
        if (!this.started || !this.ownerUid) return;
        this.timerId = setInterval(() => void this.flush(), this.config.flushIntervalMs);
    }

    private flushSoon(): void {
        if (!this.started || !this.ownerUid) return;
        Promise.resolve().then(() => void this.flush());
    }

    private async refreshQueueDepth(): Promise<void> {
        try {
            const ownerUid = this.ownerUid;
            if (!ownerUid) {
                this.setHealth({ queueDepth: 0, quarantinedItemCount: 0 });
                return;
            }
            const [queueDepth, quarantinedItemCount] = await Promise.all([
                this.store.getQueueDepth(ownerUid),
                this.store.getQuarantineCount(ownerUid),
            ]);
            this.setHealth({ queueDepth, quarantinedItemCount });
        } catch {
            this.setHealth({ status: "degraded", lastErrorCode: "storage_unavailable" });
        }
    }

    private setHealth(patch: Partial<ContributionCaptureHealth>): void {
        this.health = { ...this.health, ...patch };
        for (const listener of this.healthListeners) {
            try { listener(this.getHealth()); } catch {}
        }
    }
}

export const ContributionCapture = new ContributionCaptureService();
